// ─── Saved Options Comparison View ──────────────────────────────
// Renders the side-by-side option cards, detailed comparison table,
// investment framing, account-manager CTA, and select flow.
import React, { useMemo, useState, useEffect } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { fmt } from '../../utils/formatters';
import { useConfig } from '../../context/ConfigContext';
import { useClients } from '../../context/ClientsContext';
import { useUsers } from '../../context/UsersContext';
import api from '../../utils/api';
import {
  buildSection125Metrics,
  savedOptionsAreStale,
  savedOptionsAreOlderThan,
  buildSavedOptions,
  HEALTHCARE_ACCESS_LABELS,
  COMPLIANCE_LABELS,
} from '../../utils/savedOptionsBuilder';

const BADGE_TONES = {
  current: { bg: '#e5e7eb', color: '#374151', label: 'Current' },
  recommended: { bg: '#dbeafe', color: '#1d4ed8', label: 'Recommended' },
  alternative: { bg: '#ede9fe', color: '#6d28d9', label: 'Alternative' },
};

export default function SavedOptionsComparisonView({ client, anchor = 'saved-options-anchor' }) {
  const { config } = useConfig();
  const { saveClient } = useClients();
  const { currentUser } = useUsers();
  const savedOptions = client?.savedOptions || null;
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissedStale, setDismissedStale] = useState(false);

  // Re-derive fresh metrics so we can flag drift
  const freshMetrics = useMemo(() => buildSection125Metrics(client, config), [client, config]);
  const isStale = !dismissedStale && savedOptionsAreStale(savedOptions, freshMetrics);
  const isOld = savedOptionsAreOlderThan(savedOptions, 90);
  const selectedId = savedOptions?.selectedOptionId;

  if (!savedOptions || !savedOptions.options || savedOptions.options.length === 0) return null;

  const refreshOptions = async () => {
    if (!savedOptions || busy) return;
    setBusy(true);
    try {
      const newOpts = buildSavedOptions({
        client,
        config,
        packageId: savedOptions.sourcePackageId,
        monthlyContribution: savedOptions.sourceMonthlyContribution,
        answers: client?.ibosConfig?.section105?.answers,
        createdBy: savedOptions.createdBy,
        createdById: savedOptions.createdById,
      });
      // Preserve previous version
      const versions = savedOptions.versions || [];
      versions.push({
        version: versions.length + 1,
        savedAt: savedOptions.createdAt,
        options: savedOptions.options,
      });
      await saveClient({
        ...client,
        savedOptions: { ...newOpts, versions, selectedOptionId: null, status: 'pending' },
      });
      setDismissedStale(true);
    } finally {
      setBusy(false);
    }
  };

  const confirmSelection = async () => {
    if (!confirming || busy) return;
    setBusy(true);
    try {
      const optId = confirming.id;
      const optTitle = confirming.title;
      const userName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || 'Employer';
      const employerName = client?.company?.name || 'this employer';
      const next = {
        ...savedOptions,
        selectedOptionId: optId,
        selectedAt: new Date().toISOString(),
        selectedBy: currentUser?.id || null,
        status: 'selected',
      };
      await saveClient({ ...client, savedOptions: next });

      // Auto-message the account manager
      try {
        const optLabel = `${confirming.label} (${optTitle})`;
        await api.messages.send(
          client.id || client._id,
          `${userName} selected ${optLabel} for ${employerName}. They\u2019d like to move forward with this option.`
        );
      } catch (err) {
        // non-blocking — message will be retried by user manually if needed
      }
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id={anchor}
      data-testid="saved-options-section"
      style={{ ...S.card, padding: 24, scrollMarginTop: 88 }}
    >
      <SectionHeader title="Your benefit options" icon="bookmark-outline" />

      {/* Selection banner */}
      {selectedId && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: '#ecfdf5',
            border: `1px solid #a7f3d0`,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            color: '#065f46',
          }}
        >
          <Icon name="checkmark-circle" style={{ fontSize: 18 }} />
          <div>
            <strong>You selected {savedOptions.options.find(o => o.id === selectedId)?.label}.</strong>{' '}
            Your account manager has been notified and will follow up to finalize implementation.
          </div>
        </div>
      )}

      {/* Stale data warning */}
      {isStale && (
        <div
          data-testid="saved-options-stale-banner"
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            background: '#fff7ed',
            border: `1px solid #fed7aa`,
            borderRadius: 8,
            fontSize: 12,
            color: '#9a3412',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Icon name="warning-outline" style={{ fontSize: 18 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>Your workforce analysis has been updated</strong> since these options were saved. The numbers shown may have changed.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={refreshOptions}
              disabled={busy}
              style={{ padding: '6px 12px', background: '#9a3412', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              {busy ? 'Refreshing\u2026' : 'Refresh with latest data'}
            </button>
            <button
              type="button"
              onClick={() => setDismissedStale(true)}
              style={{ padding: '6px 12px', background: 'transparent', color: '#9a3412', border: `1px solid #fed7aa`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              Keep current
            </button>
          </div>
        </div>
      )}

      {/* Old options notice */}
      {isOld && !isStale && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: C.bg, border: `1px solid ${C.borderLight}`, borderRadius: 8, fontSize: 12, color: C.textMid }}>
          <Icon name="time-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: 'middle' }} />
          These options were generated on {formatDate(savedOptions.createdAt)}. Tax rates and workforce data may have changed.
          <button
            type="button"
            onClick={refreshOptions}
            disabled={busy}
            style={{ marginLeft: 10, padding: '4px 10px', background: C.accent, color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Option cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${savedOptions.options.length}, minmax(0, 1fr))`,
          gap: 14,
          marginBottom: 18,
        }}
      >
        {savedOptions.options.map((opt) => (
          <OptionCard
            key={opt.id}
            option={opt}
            isSelected={selectedId === opt.id}
            isLocked={!!selectedId}
            onSelect={() => setConfirming(opt)}
          />
        ))}
      </div>

      {/* Detailed comparison table */}
      <ComparisonTable options={savedOptions.options} />

      {/* Investment framing */}
      <InvestmentFraming options={savedOptions.options} />

      {/* Account manager CTA */}
      <AccountManagerCTA client={client} />

      {/* Select-confirmation modal */}
      {confirming && (
        <ConfirmSelectionModal
          option={confirming}
          onCancel={() => setConfirming(null)}
          onConfirm={confirmSelection}
          busy={busy}
        />
      )}
    </section>
  );
}

function SectionHeader({ title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <Icon name={icon} style={{ fontSize: 20, color: C.accent }} />
      <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{title}</h2>
    </div>
  );
}

// ─── Option Card ────────────────────────────────────────────────
function OptionCard({ option, isSelected, isLocked, onSelect }) {
  const tone = BADGE_TONES[option.badge] || BADGE_TONES.alternative;
  const isRecommended = option.badge === 'recommended';
  const eligibilityBg = isRecommended ? '#ecfdf5' : '#f3f4f6';
  const eligibilityColor = isRecommended ? '#065f46' : '#374151';
  const eligibilityValue = option.section105
    ? `${option.section105.newEligibleCount} of ${option.section125.totalEmployees}`
    : `${option.section125.eligibleCount} of ${option.section125.totalEmployees}`;
  const eligibilityRate = option.section105 ? option.section105.newEligibilityRate : option.section125.eligibilityRate;
  const bridgedDelta = option.section105?.bridgedEmployees > 0 ? ` (+${option.section105.bridgedEmployees})` : '';
  return (
    <div
      data-testid={`option-card-${option.id}`}
      style={{
        background: '#fff',
        border: `${isRecommended ? '2px' : '1px'} solid ${isRecommended ? C.accent : C.border}`,
        borderRadius: 12,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        position: 'relative',
        boxShadow: isRecommended ? '0 2px 8px rgba(0, 102, 204, 0.06)' : 'none',
      }}
    >
      <span
        style={{
          alignSelf: 'flex-start',
          padding: '3px 10px',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          borderRadius: 999,
          background: tone.bg,
          color: tone.color,
        }}
      >
        {tone.label}
      </span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {option.label}
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: '4px 0 2px' }}>{option.title}</h3>
        <div style={{ fontSize: 12, color: C.textMid }}>{option.subtitle}</div>
      </div>

      {/* Eligibility highlight */}
      <div
        style={{
          background: eligibilityBg,
          color: eligibilityColor,
          borderRadius: 10,
          padding: 14,
          textAlign: 'center',
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 800 }}>{eligibilityValue}</div>
        <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2 }}>
          {eligibilityRate}% eligible{bridgedDelta}
        </div>
      </div>

      {/* Key metrics */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: C.text }}>
        <KV label="Your FICA savings" value={fmt(option.totalEmployerFica)} highlight="green" />
        <KV label="Employee total value" value={fmt(option.totalEmployeeValue)} highlight="blue" />
        <KV label="Cost to you" value={option.totalEmployeeCost > 0 ? `${fmt(option.totalEmployeeCost)}/yr` : '$0'} />
        {option.section105 && (
          <KV
            label="Net after tax deduct."
            value={`${option.netEmployerPosition >= 0 ? '+' : ''}${fmt(option.netEmployerPosition)}/yr`}
            highlight={option.netEmployerPosition >= 0 ? 'green' : 'orange'}
          />
        )}
        {!option.section105 && (
          <KV label="Avg employee gain" value={`+$${option.section125.avgMonthlyBenefit}/mo`} highlight="blue" />
        )}
      </div>

      {/* Component list */}
      <div style={{ borderTop: `1px solid ${C.borderLight}`, paddingTop: 10, marginTop: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          Includes
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {option.components.map((c, i) => (
            <ComponentRow key={i} comp={c} />
          ))}
        </div>
      </div>

      {/* Action */}
      <button
        type="button"
        onClick={onSelect}
        disabled={isLocked || isSelected}
        data-testid={`option-select-${option.id}`}
        style={{
          marginTop: 6,
          padding: '10px 14px',
          background: isSelected ? C.green : isRecommended ? C.accent : '#fff',
          color: isSelected ? '#fff' : isRecommended ? '#fff' : C.text,
          border: isRecommended ? 'none' : `1px solid ${C.border}`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 800,
          cursor: isLocked ? 'not-allowed' : 'pointer',
          opacity: isLocked && !isSelected ? 0.5 : 1,
        }}
      >
        {isSelected ? '✓ Selected' : isLocked ? 'Locked — option chosen' : 'Select this option'}
      </button>
    </div>
  );
}

function ComponentRow({ comp }) {
  const isNew = comp.status === 'new';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: isNew ? C.accent : C.green, flexShrink: 0 }} />
      <span style={{ flex: 1, color: C.text }}>{comp.name}</span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          padding: '2px 6px',
          borderRadius: 4,
          background: isNew ? '#dbeafe' : '#dcfce7',
          color: isNew ? '#1d4ed8' : '#166534',
        }}
      >
        {isNew ? 'New' : 'Active'}
      </span>
    </div>
  );
}

function KV({ label, value, highlight }) {
  const colors = { green: C.green, blue: C.blue, orange: C.orange };
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: C.textMid, fontSize: 12 }}>{label}</span>
      <span style={{ color: colors[highlight] || C.text, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

// ─── Comparison Table ───────────────────────────────────────────
function ComparisonTable({ options }) {
  if (!options || options.length < 2) return null;
  const a = options[0];
  const b = options[1];
  const rows = [
    {
      label: 'Eligible employees',
      av: `${a.section125.eligibleCount} (${a.section125.eligibilityRate}%)`,
      bv: b.section105 ? `${b.section105.newEligibleCount} (${b.section105.newEligibilityRate}%)` : `${b.section125.eligibleCount} (${b.section125.eligibilityRate}%)`,
      delta: b.section105 ? `+${b.section105.bridgedEmployees}` : '—',
      tone: 'green',
    },
    {
      label: 'Your FICA savings',
      av: fmt(a.totalEmployerFica),
      bv: fmt(b.totalEmployerFica),
      delta: deltaCurrency(b.totalEmployerFica - a.totalEmployerFica),
      tone: b.totalEmployerFica >= a.totalEmployerFica ? 'green' : 'orange',
    },
    {
      label: 'Employee value',
      av: fmt(a.totalEmployeeValue),
      bv: fmt(b.totalEmployeeValue),
      delta: deltaCurrency(b.totalEmployeeValue - a.totalEmployeeValue),
      tone: 'blue',
    },
    {
      label: 'Your cost',
      av: a.totalEmployeeCost > 0 ? fmt(a.totalEmployeeCost) : '$0',
      bv: b.totalEmployeeCost > 0 ? `-${fmt(b.totalEmployeeCost)}` : '$0',
      delta: 'investment',
      tone: 'neutral',
    },
    {
      label: 'Net after FICA + tax',
      av: `+${fmt(a.netEmployerPosition)}`,
      bv: `${b.netEmployerPosition >= 0 ? '+' : ''}${fmt(b.netEmployerPosition)}`,
      delta: '',
      tone: b.netEmployerPosition >= 0 ? 'green' : 'orange',
    },
    {
      label: 'Healthcare access',
      av: HEALTHCARE_ACCESS_LABELS[a.healthcareAccess] || a.healthcareAccess,
      bv: HEALTHCARE_ACCESS_LABELS[b.healthcareAccess] || b.healthcareAccess,
      delta: '',
      tone: 'neutral',
    },
    {
      label: 'Compliance strength',
      av: COMPLIANCE_LABELS[a.complianceStrength] || a.complianceStrength,
      bv: COMPLIANCE_LABELS[b.complianceStrength] || b.complianceStrength,
      delta: '',
      tone: 'neutral',
    },
  ];

  return (
    <div data-testid="saved-options-table" style={{ marginTop: 4, marginBottom: 18, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: C.bg }}>
            <th style={tableTh}></th>
            <th style={{ ...tableTh, color: C.textMid }}>{a.label}</th>
            <th style={{ ...tableTh, color: C.accent, background: '#dbeafe' }}>⭐ {b.label}</th>
            <th style={tableTh}>Difference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.label} style={{ background: i % 2 === 0 ? '#fff' : C.bg }}>
              <td style={{ ...tableTd, fontWeight: 700, color: C.textMid }}>{r.label}</td>
              <td style={tableTd}>{r.av}</td>
              <td style={{ ...tableTd, fontWeight: 700, color: C.text, background: '#eff6ff' }}>{r.bv}</td>
              <td style={{ ...tableTd, color: deltaColor(r.tone) }}>{r.delta}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const tableTh = { padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textLight, borderBottom: `1px solid ${C.border}`, textAlign: 'left' };
const tableTd = { padding: '10px 14px', borderBottom: `1px solid ${C.borderLight}`, color: C.text };
function deltaCurrency(n) {
  if (n === 0) return '—';
  return `${n > 0 ? '+' : ''}${fmt(n)}`;
}
function deltaColor(tone) {
  if (tone === 'green') return C.green;
  if (tone === 'blue') return C.blue;
  if (tone === 'orange') return C.orange;
  return C.textMid;
}

// ─── Investment Framing (Option B only) ─────────────────────────
function InvestmentFraming({ options }) {
  const b = options.find((o) => o.section105);
  if (!b || !b.section105) return null;
  const monthlyContribution = b.section105.monthlyContribution || 0;
  const taxBack = Math.round(monthlyContribution * 0.273);
  const ficaPerEe = Math.round((b.totalEmployerFica - (options[0]?.totalEmployerFica || 0)) / Math.max(1, b.section105.newEligibleCount) / 12);
  const netReal = Math.max(0, monthlyContribution - taxBack - Math.max(0, ficaPerEe));
  const bridged = b.section105.bridgedEmployees;
  return (
    <div
      data-testid="saved-options-investment-framing"
      style={{
        marginBottom: 18,
        padding: 20,
        background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
        border: `1px solid ${C.accentLight}`,
        borderRadius: 12,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, color: C.accent, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        The math on {b.label}
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text, margin: '0 0 12px' }}>
        Your real cost is much less than the sticker price
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
        <Stat label="You invest" value={`$${monthlyContribution}/ee/mo`} tone="neutral" />
        <Stat label="Tax deduction returns" value={`~$${taxBack}/ee/mo`} tone="green" />
        <Stat label="Additional FICA savings" value={`~$${Math.max(0, ficaPerEe)}/ee/mo`} tone="green" />
        <Stat label="Net real cost" value={`~$${netReal}/ee/mo`} tone="accent" emphasised />
      </div>
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
        For that <strong>~${netReal}/ee/mo</strong>, each employee gets:
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          <li>Tax-free healthcare funds (${monthlyContribution}/mo)</li>
          {b.coverageLayer && <li>{b.coverageLayer.name} preventive care coverage</li>}
          <li>All HealthCues program benefits</li>
          <li>Take-home pay increase on top</li>
        </ul>
        {bridged > 0 && (
          <div style={{ marginTop: 10, color: C.green, fontWeight: 700 }}>
            <Icon name="trending-up" style={{ fontSize: 14, marginRight: 4, verticalAlign: 'middle' }} />
            And {bridged} employee{bridged !== 1 ? 's' : ''} who couldn\u2019t participate before now can.
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, emphasised }) {
  const toneColor = tone === 'green' ? C.green : tone === 'accent' ? C.accent : C.text;
  return (
    <div
      style={{
        background: '#fff',
        border: `${emphasised ? '2px' : '1px'} solid ${emphasised ? C.accent : C.border}`,
        borderRadius: 10,
        padding: '10px 14px',
        textAlign: 'left',
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: toneColor, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}

// ─── Account Manager CTA ────────────────────────────────────────
function AccountManagerCTA({ client }) {
  const [user, setUser] = useState(null);
  const assignedTo = client?.crm?.assignedTo;
  useEffect(() => {
    let cancelled = false;
    if (!assignedTo) return undefined;
    (async () => {
      try {
        const list = await api.users.list();
        const m = list.find((u) => u.id === assignedTo || u._id === assignedTo);
        if (!cancelled) setUser(m || null);
      } catch (err) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [assignedTo]);
  const name = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'your HealthCues team';
  const employerName = client?.company?.name || '';
  const initials = (() => {
    if (!user) return '?';
    const f = user.firstName?.[0] || '';
    const l = user.lastName?.[0] || '';
    return ((f + l).toUpperCase()) || (user.email?.slice(0, 2).toUpperCase() || '?');
  })();

  const onClick = () => {
    // Try to scroll to the Messages section. The Portal renders MessagesSection
    // with the same structure for both employer and AM.
    const msgs = document.querySelector('[data-testid="messages-section"]');
    if (msgs) msgs.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Optionally pre-fill via sessionStorage so Messages can pick it up
    try {
      sessionStorage.setItem(
        `s105-prefill-${client?.id || ''}`,
        `Hi ${user?.firstName || 'there'}, I\u2019ve been reviewing the two benefit options${employerName ? ` for ${employerName}` : ''} and have some questions.`
      );
    } catch (err) { /* ignore */ }
  };

  return (
    <div
      data-testid="saved-options-am-cta"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: 14,
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: C.accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>
          {user ? `Need help deciding? Talk to ${user.firstName}` : 'Have questions? Message your HealthCues team'}
        </div>
        <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
          {user ? `${name} can walk you through both options and answer any questions.` : 'Your HealthCues team is here to help you compare the options.'}
        </div>
      </div>
      <Icon name="arrow-forward" style={{ fontSize: 18, color: C.accent }} />
    </div>
  );
}

// ─── Confirm Selection Modal ────────────────────────────────────
function ConfirmSelectionModal({ option, onCancel, onConfirm, busy }) {
  return (
    <div
      data-testid="confirm-selection-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: 0 }}>Confirm your selection</h3>
        <p style={{ fontSize: 13, color: C.textMid, marginTop: 6, marginBottom: 16, lineHeight: 1.6 }}>
          You\u2019re selecting <strong style={{ color: C.text }}>{option.label}</strong> — {option.title}.
        </p>
        <div style={{ background: C.bg, border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Your account manager will follow up to:
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: C.text, lineHeight: 1.7 }}>
            <li>Finalize the plan details</li>
            <li>Prepare the necessary documents</li>
            <li>Set an implementation timeline</li>
          </ul>
        </div>
        <p style={{ fontSize: 12, color: C.textLight, marginBottom: 16 }}>
          This doesn\u2019t commit you to anything — it lets your team know you\u2019d like to move forward with this option.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={busy} style={{ padding: '10px 16px', background: 'transparent', color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} data-testid="confirm-selection-btn" style={{ padding: '10px 18px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Saving\u2026' : 'Confirm selection'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (err) {
    return '';
  }
}
